// @ts-strict-ignore
import loadable from '@loadable/component'
import { ExperienceEntitySelector, Container } from './react'
import { Palette } from '../../utils/palette'

interface Component {
  variations: object
  wrapperConfig?: object
  hydrate?: boolean | object
}

interface ComponentMap {
  [key: string]: Component
}

export const Components: ComponentMap = {
  author: {
    variations: {
      default: loadable(() => import('../author')),
    },
    wrapperConfig: { useContainer: false },
  },
  checklist: {
    variations: {
      default: loadable(() => import('../checklist')),
    },
    wrapperConfig: {},
  },
  readMore: {
    variations: {
      default: loadable(() => import('../read-more-card')),
    },
  },
  cta: {
    variations: {
      default: loadable(() => import('../cta')),
    },
    hydrate: false,
    wrapperConfig: { useContainer: false },
  },
  iframeModule: {
    variations: {
      default: loadable(() => import('../iframe')),
    },
    wrapperConfig: { useContainer: false, spacingBottom: '1.5em' },
    hydrate: true,
  },
  list: {
    variations: {
      default: loadable(() => import('../list')),
    },
  },
  listItem: {
    variations: {
      default: loadable(() => import('../list-item')),
    },
  },
  widgetHtml: {
    variations: {
      default: loadable(() => import('../widget-html')),
    },
    wrapperConfig: { spacingY: ['sm', 'md'] },
    hydrate: false,
  },
  richTextModule: {
    variations: {
      default: loadable(() => import('../rich-text-module')),
    },
    wrapperConfig: { useContainer: false, spacingTop: 'lg' },
    hydrate: true,
  },
  embeddedAsset: {
    variations: {
      default: loadable(() => import('../embedded-asset')),
    },
  },
  categoryGuidesModule: {
    variations: {
      default: loadable(() => import('../category-guides-module')),
    },
    wrapperConfig: { spacingTop: 'lg', spacingBottom: 'lg' },
    hydrate: { layout: 'carousel', variant: 'carousel' },
  },
  featuredCards: {
    variations: {
      default: loadable(() => import('../featured-cards')),
    },
  },
  imageCalloutModule: {
    variations: {
      default: loadable(() => import('../image-callout-module')),
    },
    wrapperConfig: { useContainer: false },
  },
  readMoreModule: {
    variations: {
      default: loadable(() => import('../read-more-module')),
    },
    wrapperConfig: {},
  },
  reviews: {
    variations: {
      default: loadable(() => import('../reviews')),
    },
    wrapperConfig: {},
    hydrate: true,
  },
  imageModule: {
    variations: {
      default: loadable(() => import('../image-module')),
    },
    wrapperConfig: { spacingTop: 0, spacingBottom: [12, 24] },
  },
  linkList: {
    variations: {
      default: loadable(() => import('../link-list')),
    },
    wrapperConfig: { useContainer: false },
  },
  card: {
    variations: {
      default: loadable(() => import('../card')),
    },
  },
  cookieToggle: {
    variations: {
      default: loadable(() => import('../cookie-toggle')),
    },
    hydrate: true,
  },
  articleIntro: {
    variations: {
      default: loadable(() => import('../article-intro')),
    },
    wrapperConfig: { useContainer: false, spacingY: 0 },
    hydrate: true,
  },
  authorGuidesModule: {
    variations: {
      default: loadable(() => import('../category-guides-module')),
    },
    wrapperConfig: { spacingTop: 'lg', spacingBottom: 'lg' },
  },
  authorProfile: {
    variations: {
      default: loadable(() => import('../author-profile')),
    },
    wrapperConfig: { useContainer: false, spacingTop: 'xxl' },
  },
  table: {
    features: { localDates: true, globalDates: true },
    variations: {
      default: loadable(() => import('../table')),
    },
    hydrate: { tableVariant: ['rates', 'rates-comparison'] },
  },
  breadcrumb: {
    variations: {
      default: loadable(() => import('../breadcrumbs.connected')),
    },
    hydrate: false,
  },
  richTextBlock: {
    variations: {
      default: loadable(() => import('../rich-text-block')),
    },
    wrapperConfig: {
      childSpacing: true,
      useContainer: false,
      noWrapper: true,
    },
    hydrate: false,
  },
  clickOutLinks: {
    variations: {
      default: loadable(() => import('../click-out-links')),
    },
    hydrate: false,
  },
  categoryHeadingModule: {
    variations: {
      default: loadable(() => import('../category-heading-module')),
    },
    wrapperConfig: { useContainer: false },
    hydrate: false,
  },
  button: {
    variations: {
      default: loadable(() => import('../button-link')),
    },
    wrapperConfig: { spacingY: ['xs', 'xs'] },
    hydrate: true,
  },
  hero: {
    variations: {
      default: loadable(() => import('../hero/hero-module.connected')),
    },
    hydrate: false,
    wrapperConfig: { useContainer: false, spacingTop: [0, 0] },
  },
  heroFlex: {
    variations: {
      default: loadable(() => import('../hero-flex')),
    },
    wrapperConfig: { spacingY: [0, 0] },
    hydrate: false,
  },
  heroCard: {
    variations: {
      default: loadable(() => import('../hero-card')),
    },
    hydrate: false,
  },
  heroAd: {
    variations: {
      default: loadable(() => import('../hero-ad.connected')),
    },
    hydrate: false,
  },
  iconTile: {
    variations: {
      default: loadable(() => import('../icon-tile')),
    },
    wrapperConfig: { spacingY: ['xs', 'xs'] },
    hydrate: true,
  },
  sponsoredByTag: {
    variations: {
      default: loadable(() => import('../sponsored-by.connected')),
    },
    hydrate: false,
  },
  component: {
    variations: {
      default: loadable(() => import('../component')),
    },
    wrapperConfig: { useContainer: false },
    hydrate: true,
  },
  billboard: {
    variations: {
      default: loadable(() => import('../billboard/billboard.connected')),
    },
    wrapperConfig: { spacingY: '0' },
    hydrate: false,
  },
  collectionModule: {
    variations: {
      default: loadable(() => import('../collection-module')),
    },
    hydrate: { layout: ['carousel', 'tabs'] },
    wrapperConfig: { spacingY: '0' },
  },
  accordion: {
    variations: {
      default: loadable(() => import('../accordion')),
    },
    hydrate: false,
  },
  quoteBlock: {
    variations: {
      default: loadable(() => import('../quote-block')),
    },
    wrapperConfig: { useContainer: false },
    hydrate: false,
  },
  providersLogoModule: {
    variations: {
      default: loadable(() => import('../providers-logo-modules')),
    },
    hydrate: false,
  },
  platinumModules: {
    variations: {
      default: loadable(() => import('../platinum-modules')),
    },
    wrapperConfig: {
      variants: {
        'ppc-nav': { spacingY: ['0', '0'] },
        'card-carousel': { spacingBottom: ['md', 0] },
      },
    },
  },
  callOut: {
    variations: {
      default: loadable(() => import('../callout')),
    },
    wrapperConfig: {
      variants: {
        'scam-warning': { useContainer: false },
      },
    },
  },
  aggregationTable: {
    variations: {
      default: loadable(() => import('../aggregation-table')),
    },
  },
  embeddedVideo: {
    variations: {
      default: loadable(() => import('../embedded-video')),
    },
  },
  palette: {
    variations: {
      default: Palette,
    },
  },
  container: {
    variations: {
      default: Container,
    },
  },
  experienceEntitySelector: {
    variations: {
      default: ExperienceEntitySelector,
    },
  },
  radioSelector: {
    variations: {
      default: loadable(() => import('../radio-selector')),
    },
  },
  conditionalModule: {
    variations: {
      default: loadable(() => import('../conditional-module')),
    },
    wrapperConfig: { useContainer: false },
  },
}
